import { getQueryString, hostname, numToSI } from "@utils/helpers";
import { amAdmin, amMod } from "@utils/roles";
import { isBrowser } from "@utils/browser";
import { Component, FormEvent, InfernoNode } from "inferno";
import { T } from "inferno-i18next-dess";
import {
  BlockCommunity,
  CommunityModeratorView,
  CommunityNotificationsMode,
  CommunityView,
  FollowCommunity,
  Language,
  ListCommunities,
  MyUserInfo,
  PersonView,
  PurgeCommunity,
  RemoveCommunity,
  EditCommunityNotifications,
} from "lemmy-js-client";
import { mdToHtml } from "@utils/markdown";
import { HttpService, I18NextService } from "../../services";
import { aimooT } from "@utils/aimoo-i18n";
import { CommunityBadges } from "../common/badges";
import { BannerIconHeader } from "../common/banner-icon-header";
import { Icon } from "../common/icon";
import { PictrsImage } from "../common/pictrs-image";
import { SubscribeButton } from "../common/subscribe-button";
import {
  CommunityLink,
  CommunitySettingsLink,
  communityLink,
} from "./community-link";
import { PersonListing } from "../person/person-listing";
import { tippyMixin } from "../mixins/tippy-mixin";
import CommunityReportModal from "@components/common/modal/community-report-modal";
import { CommunityNotificationsDropdown } from "@components/common/notifications-dropdown";
import { LanguageList } from "@components/common/language-list";
import { canViewCommunity, reportToast } from "@utils/app";
import { CreatePostButton } from "@components/common/content-actions/create-item-buttons";
import ModActionFormModal from "@components/common/modal/mod-action-form-modal";
import { Link } from "inferno-router";
import { RouterContext } from "inferno-router";

type ListCommunitiesWithCategory = ListCommunities & {
  category_id?: number;
};

const RELATED_AVATAR_GRADIENTS = [
  "aimoo-avatar-1",
  "aimoo-avatar-2",
  "aimoo-avatar-3",
  "aimoo-avatar-4",
  "aimoo-avatar-5",
  "aimoo-avatar-6",
];

function relatedAvatarGradientClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash =
      (hash + name.charCodeAt(i) * (i + 1)) % RELATED_AVATAR_GRADIENTS.length;
  }
  return RELATED_AVATAR_GRADIENTS[hash] ?? RELATED_AVATAR_GRADIENTS[0];
}

interface SidebarProps {
  communityView: CommunityView;
  moderators: CommunityModeratorView[];
  admins: PersonView[];
  allLanguages: Language[];
  siteLanguages: number[];
  communityLanguages?: number[];
  enableNsfw?: boolean;
  showIcon?: boolean;
  hideButtons?: boolean;
  /** Aimoo community detail layout (about / rules / mods cards). */
  aimooLayout?: boolean;
  myUserInfo: MyUserInfo | undefined;
  removeLoading: boolean;
  purgeLoading: boolean;
  followLoading: boolean;
  onRemove: (form: RemoveCommunity) => void;
  onPurge: (form: PurgeCommunity) => void;
  onFollow: (form: FollowCommunity) => void;
  onBlock: (form: BlockCommunity) => void;
  onEditNotifs: (form: EditCommunityNotifications) => void;
}

interface SidebarState {
  showRemoveDialog: boolean;
  removeExpires?: string;
  showPurgeDialog: boolean;
  showReportModal: boolean;
  searchText: string;
  notifications: CommunityNotificationsMode;
  relatedCommunities: CommunityView[];
}

@tippyMixin
export class CommunitySidebar extends Component<SidebarProps, SidebarState> {
  state: SidebarState = {
    showReportModal: false,
    searchText: "",
    notifications:
      this.props.communityView?.community_actions?.notifications ??
      "replies_and_mentions",
    showRemoveDialog: false,
    showPurgeDialog: false,
    relatedCommunities: [],
  };

  private relatedFetchToken?: symbol;

  componentDidMount() {
    if (this.props.aimooLayout) {
      this.fetchRelatedCommunities();
    }
  }

  componentDidUpdate(prevProps: SidebarProps) {
    if (
      this.props.aimooLayout &&
      prevProps.communityView.community.id !==
        this.props.communityView.community.id
    ) {
      this.fetchRelatedCommunities();
    }
  }

  render() {
    if (this.props.aimooLayout) {
      return this.renderAimoo();
    }
    return this.renderDefault();
  }

  async fetchRelatedCommunities() {
    if (!isBrowser()) {
      return;
    }
    const community = this.props.communityView.community;
    const categoryId = (community as { category_id?: number }).category_id;
    const token = (this.relatedFetchToken = Symbol());
    const form: ListCommunitiesWithCategory = {
      type_: "local",
      sort: "hot",
      limit: 8,
      ...(categoryId !== undefined ? { category_id: categoryId } : {}),
    };
    const res = await HttpService.client.listCommunities(form);
    if (token !== this.relatedFetchToken) {
      return;
    }
    if (res.state === "success") {
      this.setState({
        relatedCommunities: res.data.items
          .filter(cv => cv.community.id !== community.id)
          .slice(0, 5),
      });
    }
  }

  renderAimoo(): InfernoNode {
    const { communityView } = this.props;
    const { community } = communityView;
    const {
      summary,
      posting_restricted_to_mods,
      published,
      subscribers,
      posts,
      sidebar,
    } = community;
    const createdAt = published?.substring(0, 10);

    return (
      <div className="community-sidebar aimoo-community-detail__sidebar">
        <aside className="aimoo-community-detail__sidebar-inner">
          {posting_restricted_to_mods && (
            <div
              className="alert alert-warning aimoo-community-detail__alert"
              role="alert"
            >
              <Icon icon="lock" inline classes="me-2" />
              <T i18nKey="community_locked_message" className="d-inline">
                #<strong className="fw-bold">#</strong>#
              </T>
            </div>
          )}

          <section className="aimoo-sidebar-card">
            <h4 className="aimoo-sidebar-card__title">
              {aimooT("about_community")}
            </h4>
            {summary ? (
              <p className="aimoo-community-detail__about-text">{summary}</p>
            ) : (
              <p className="aimoo-sidebar-card__empty">{aimooT("no_summary")}</p>
            )}
            <div className="aimoo-community-detail__about-stats">
              {createdAt && (
                <div className="aimoo-community-detail__about-row">
                  <span>{aimooT("created_time")}</span>
                  <span>{createdAt}</span>
                </div>
              )}
              <div className="aimoo-community-detail__about-row">
                <span>{aimooT("member_count_label")}</span>
                <span>{numToSI(subscribers)}</span>
              </div>
              <div className="aimoo-community-detail__about-row">
                <span>{aimooT("post_count_label")}</span>
                <span>{numToSI(posts)}</span>
              </div>
            </div>
          </section>

          {sidebar && (
            <section className="aimoo-sidebar-card">
              <h4 className="aimoo-sidebar-card__title">
                {aimooT("community_rules")}
              </h4>
              <div
                className="md-div aimoo-community-detail__rules"
                dangerouslySetInnerHTML={mdToHtml(sidebar, () =>
                  this.forceUpdate(),
                )}
              />
            </section>
          )}

          {this.aimooMods()}

          {this.aimooRelatedCommunities()}
        </aside>
      </div>
    );
  }

  aimooMods(): InfernoNode {
    if (!this.props.moderators.length) {
      return undefined;
    }

    return (
      <section className="aimoo-sidebar-card">
        <h4 className="aimoo-sidebar-card__title">{aimooT("admins")}</h4>
        <div className="aimoo-community-detail__mods">
          {this.props.moderators.map((mod, index) => (
            <div key={mod.moderator.id} className="aimoo-community-detail__mod">
              <PersonListing
                person={mod.moderator}
                banned={false}
                myUserInfo={this.props.myUserInfo}
                muted={false}
              />
              <span className="aimoo-community-detail__mod-role">
                {index === 0 ? aimooT("moderator") : aimooT("manager")}
              </span>
            </div>
          ))}
        </div>
      </section>
    );
  }

  aimooRelatedCommunities(): InfernoNode {
    const { relatedCommunities } = this.state;

    return (
      <section className="aimoo-sidebar-card">
        <h4 className="aimoo-sidebar-card__title">
          {aimooT("related_communities")}
        </h4>
        {relatedCommunities.length === 0 ? (
          <p className="aimoo-sidebar-card__empty">{aimooT("no_related")}</p>
        ) : (
          <div className="aimoo-sidebar-card__list">
            {relatedCommunities.map(cv => {
              const community = cv.community;
              const title = community.title ?? community.name;
              const initial = title.trim().charAt(0).toUpperCase() || "?";
              return (
                <Link
                  key={community.id}
                  to={communityLink(community).link}
                  className="aimoo-sidebar-item"
                >
                  {community.icon ? (
                    <PictrsImage
                      src={community.icon}
                      type="icon"
                      nsfw={community.nsfw}
                    />
                  ) : (
                    <div
                      className={`aimoo-sidebar-item__avatar ${relatedAvatarGradientClass(
                        community.name,
                      )}`}
                    >
                      {initial}
                    </div>
                  )}
                  <div className="aimoo-sidebar-item__text">
                    <p className="aimoo-sidebar-item__name">{title}</p>
                    <p className="aimoo-sidebar-item__meta">
                      {aimooT("members_count", {
                        count: numToSI(community.subscribers),
                      })}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    );
  }

  renderDefault() {
    const { communityView, myUserInfo } = this.props;
    const {
      community,
      community: {
        name,
        ap_id,
        summary,
        posting_restricted_to_mods,
        visibility,
      },
      community_actions: { received_ban_at } = {},
    } = communityView;

    const visibilityLabel = `community_visibility_${visibility}` as const;
    const visibilityDescription = `${visibilityLabel}_desc` as const;
    const canViewCommunity_ = canViewCommunity(communityView);

    return (
      <div className="community-sidebar">
        <aside className="mb-3">
          <div id="sidebarContainer">
            {!this.props.hideButtons && (
              <section id="sidebarMain" className="card mb-3">
                <div className="card-body">
                  {this.communityTitle()}
                  {summary && <h6>{summary}</h6>}
                  {received_ban_at && (
                    <div
                      className="alert alert-danger text-sm-start text-xs-center mt-2"
                      role="alert"
                    >
                      <Icon
                        icon="ban"
                        inline
                        classes="me-sm-2 mx-auto d-sm-inline d-block"
                      />
                      <T
                        i18nKey="banned_from_community_blurb"
                        className="d-inline"
                      >
                        #<strong className="fw-bold">#</strong>#
                      </T>
                    </div>
                  )}
                  {!received_ban_at && (
                    <>
                      <SubscribeButton
                        followState={
                          communityView.community_actions?.follow_state
                        }
                        apId={community.ap_id}
                        onFollow={() => handleFollow(this, true)}
                        onUnFollow={() => handleFollow(this, false)}
                        loading={this.props.followLoading}
                        showRemoteFetch={!this.props.myUserInfo}
                      />
                      {this.canPost() && canViewCommunity_ && (
                        <CreatePostButton
                          communityView={communityView}
                          myUserInfo={myUserInfo}
                        />
                      )}
                    </>
                  )}
                  <button
                    className="btn btn-light border-light-subtle d-block mb-2 w-100"
                    onClick={() => handleShowReportModal(this)}
                  >
                    {I18NextService.i18n.t("create_report")}
                  </button>
                  <CommunityReportModal
                    onSubmit={reason => handleSubmitReport(this, reason)}
                    onCancel={() => handleHideReportModal(this)}
                    show={this.state.showReportModal}
                  />
                  <Link
                    className="btn btn-light border-light-subtle d-block mb-2 w-100"
                    to={`/modlog?communityId=${community.id}`}
                  >
                    {I18NextService.i18n.t("modlog")}
                  </Link>
                  {this.amModOrAdminAndLocal() && (
                    <CommunitySettingsLink community={community} />
                  )}
                  {amAdmin(myUserInfo) && (
                    <>
                      <button
                        className="btn btn-light border-light-subtle d-block mb-2 w-100"
                        onClick={() => handleShowRemoveDialog(this)}
                      >
                        {I18NextService.i18n.t(
                          !communityView.community.removed
                            ? "remove"
                            : "restore",
                        )}
                      </button>
                      <ModActionFormModal
                        onSubmit={reason => handleRemove(this, reason)}
                        modActionType="remove-community"
                        isRemoved={communityView.community.removed}
                        onCancel={() => handleHideRemoveDialog(this)}
                        show={this.state.showRemoveDialog}
                        loading={false}
                      />
                      <button
                        className="btn btn-light border-light-subtle d-block mb-2 w-100"
                        onClick={() => handleShowPurgeDialog(this)}
                        aria-label={I18NextService.i18n.t("purge_community")}
                      >
                        {I18NextService.i18n.t("purge_community")}
                      </button>
                      <ModActionFormModal
                        onSubmit={reason => handlePurge(this, reason)}
                        modActionType="purge-community"
                        community={communityView.community}
                        onCancel={() => handleHidePurgeDialog(this)}
                        show={this.state.showPurgeDialog}
                        loading={false}
                      />
                    </>
                  )}
                  <>
                    {this.props.myUserInfo && this.blockCommunity()}
                    {canViewCommunity_ && (
                      <>
                        <div className="d-block mb-2">
                          <CommunityNotificationsDropdown
                            currentOption={this.state.notifications}
                            onSelect={val =>
                              handleNotificationChange(this, val)
                            }
                            className="btn btn-light border-light-subtle d-block w-100 text-wrap"
                          />
                        </div>
                        <form
                          className="d-flex"
                          onSubmit={e => handleSearchSubmit(this, e)}
                        >
                          <input
                            name="q"
                            type="search"
                            className="form-control flex-initial"
                            placeholder={`${I18NextService.i18n.t("search")}...`}
                            aria-label={I18NextService.i18n.t("search")}
                            onInput={e => handleSearchChange(this, e)}
                            required
                            minLength={1}
                          />
                          <button
                            type="submit"
                            className="btn btn-outline-secondary ms-1"
                          >
                            <Icon icon="search" />
                          </button>
                        </form>
                      </>
                    )}
                  </>
                  {!this.props.myUserInfo && (
                    <div className="alert alert-info" role="alert">
                      <T
                        i18nKey="community_not_logged_in_alert"
                        interpolation={{
                          community: name,
                          instance: hostname(ap_id),
                        }}
                      >
                        #<code className="user-select-all">#</code>#
                      </T>
                    </div>
                  )}
                </div>
              </section>
            )}
            <section id="sidebarInfo" className="card mb-3">
              <div className="card-body">
                {posting_restricted_to_mods && (
                  <div
                    className="alert alert-warning text-sm-start text-xs-center"
                    role="alert"
                  >
                    <Icon
                      icon="lock"
                      inline
                      classes="me-sm-2 mx-auto d-sm-inline d-block"
                    />
                    <T i18nKey="community_locked_message" className="d-inline">
                      #<strong className="fw-bold">#</strong>#
                    </T>
                  </div>
                )}
                {this.sidebarMarkdown()}
                <div>
                  <div className="fw-semibold mb-1">
                    <span className="align-middle">
                      {I18NextService.i18n.t("community_visibility")}:&nbsp;
                    </span>
                    <span className="fs-5 fw-medium align-middle">
                      {I18NextService.i18n.t(visibilityLabel)}
                    </span>
                  </div>
                  <p>{I18NextService.i18n.t(visibilityDescription)}</p>
                </div>
                <LanguageList
                  allLanguages={this.props.allLanguages}
                  languageIds={this.props.siteLanguages}
                />
                <CommunityBadges community={communityView.community} />
                {this.mods()}
              </div>
            </section>
          </div>
        </aside>
      </div>
    );
  }

  communityTitle() {
    const community = this.props.communityView.community;

    return (
      <div>
        <h2 className="h5 mb-0">
          {this.props.showIcon && !community.removed && (
            <BannerIconHeader icon={community.icon} banner={community.banner} />
          )}
          <span className="me-2">
            <CommunityLink
              community={community}
              hideAvatar
              myUserInfo={this.props.myUserInfo}
              muted={false}
            />
          </span>
          {community.removed && (
            <small className="me-2 text-muted fst-italic">
              {I18NextService.i18n.t("removed")}
            </small>
          )}
          {community.deleted && (
            <small className="me-2 text-muted fst-italic">
              {I18NextService.i18n.t("deleted")}
            </small>
          )}
          {community.nsfw && (
            <small className="me-2 text-muted fst-italic">
              {I18NextService.i18n.t("nsfw")}
            </small>
          )}
        </h2>
        <CommunityLink
          community={community}
          realLink
          useApubName
          muted
          hideAvatar
          myUserInfo={this.props.myUserInfo}
        />
      </div>
    );
  }

  mods() {
    if (!this.props.moderators.length) {
      return;
    }

    return (
      <ul className="list-inline small">
        <li className="list-inline-item">{I18NextService.i18n.t("mods")}: </li>
        {this.props.moderators.map(mod => (
          <li key={mod.moderator.id} className="list-inline-item">
            <PersonListing
              person={mod.moderator}
              banned={false}
              myUserInfo={this.props.myUserInfo}
              muted={false}
            />
          </li>
        ))}
      </ul>
    );
  }

  blockCommunity() {
    const { community_actions: { follow_state: subscribed, blocked_at } = {} } =
      this.props.communityView;

    return (
      !subscribed && (
        <button
          className="btn btn-danger d-block mb-2 w-100"
          onClick={() => handleBlock(this)}
        >
          {I18NextService.i18n.t(
            blocked_at ? "unblock_community" : "block_community",
          )}
        </button>
      )
    );
  }

  sidebarMarkdown() {
    const { sidebar } = this.props.communityView.community;
    return (
      sidebar && (
        <div
          className="md-div"
          dangerouslySetInnerHTML={mdToHtml(sidebar, () => this.forceUpdate())}
        />
      )
    );
  }

  canPost(): boolean {
    return (
      !this.props.communityView.community.posting_restricted_to_mods ||
      this.amModOrAdmin()
    );
  }

  amModOrAdmin(): boolean {
    return amMod(this.props.communityView) || amAdmin(this.props.myUserInfo);
  }

  /**
   * Only allow viewing community sidebar if you're a mod, or you're an admin and its a local community
   **/
  amModOrAdminAndLocal(): boolean {
    return (
      amMod(this.props.communityView) ||
      (amAdmin(this.props.myUserInfo) &&
        this.props.communityView.community.local)
    );
  }
}

function handleSearchChange(
  i: CommunitySidebar,
  event: FormEvent<HTMLInputElement>,
) {
  i.setState({ searchText: event.target.value });
}

function handleSearchSubmit(
  i: CommunitySidebar,
  event: FormEvent<HTMLFormElement>,
) {
  event.preventDefault();
  const searchParamEncoded = i.state.searchText;
  const context = i.context as RouterContext;
  context.router.history.push(
    `/search${getQueryString({ q: searchParamEncoded, communityId: i.props.communityView.community.id.toString() })}`,
  );
}

function handleShowReportModal(i: CommunitySidebar) {
  i.setState({ showReportModal: true });
}

function handleHideReportModal(i: CommunitySidebar) {
  i.setState({ showReportModal: false });
}

async function handleSubmitReport(i: CommunitySidebar, reason: string) {
  const res = await HttpService.client.createCommunityReport({
    community_id: i.props.communityView.community.id,
    reason,
  });
  reportToast(res);
}

function handleShowRemoveDialog(i: CommunitySidebar) {
  i.setState({ showRemoveDialog: true });
}

function handleHideRemoveDialog(i: CommunitySidebar) {
  i.setState({ showRemoveDialog: false });
}

function handleRemove(i: CommunitySidebar, reason: string) {
  i.setState({ showRemoveDialog: false });
  i.props.onRemove({
    community_id: i.props.communityView.community.id,
    removed: !i.props.communityView.community.removed,
    reason,
  });
}

function handleShowPurgeDialog(i: CommunitySidebar) {
  i.setState({ showPurgeDialog: true });
}

function handleHidePurgeDialog(i: CommunitySidebar) {
  i.setState({ showPurgeDialog: false });
}

function handlePurge(i: CommunitySidebar, reason: string) {
  i.props.onPurge({
    community_id: i.props.communityView.community.id,
    reason,
  });
  i.setState({ showPurgeDialog: false });
}

function handleNotificationChange(
  i: CommunitySidebar,
  val: CommunityNotificationsMode,
) {
  i.setState({ notifications: val });
  const form = {
    community_id: i.props.communityView.community.id,
    mode: i.state.notifications,
  };
  i.props.onEditNotifs(form);
}

function handleFollow(i: CommunitySidebar, follow: boolean) {
  i.props.onFollow({
    community_id: i.props.communityView.community.id,
    follow,
  });
}

function handleBlock(i: CommunitySidebar) {
  const { community, community_actions: { blocked_at } = {} } =
    i.props.communityView;

  i.props.onBlock({
    community_id: community.id,
    block: !blocked_at,
  });
}
